import type { CartItemPut } from '~/utils/easyApi'

export function useCartActions() {
  const cartStore = useCart()
  const { $snackbar, $i18n, $log, $storeErrorLog } = useNuxtApp()
  const { trackAddToCart, trackRemoveFromCart } = useCartTracking()

  // Add or Update an Item in the Cart
  async function addOrUpdateItem(
    supplierHandle: string,
    item: CartItemPut,
    showSnackBar: boolean = false,
    trackingCtx?: { index?: number, itemListId?: string, itemListName?: string, pageSection?: string },
  ) {
    const previousQuantity = cartStore.carts
      .find(c => c.supplier?.handle === supplierHandle)
      ?.items.find(ci => ci.handle === item.handle && ci.reference === item.reference)
      ?.quantity ?? 0

    try {
      await cartStore.updateCartItem(supplierHandle, item)

      if (showSnackBar) {
        $snackbar.showMessage({
          text: $i18n.t('addToCart.success'),
          color: 'success',
          closeText: $i18n.t('close'),
        })
      }
      return true
    }

    catch (error) {
      handleCartError(error, 'Failed to add or update item')
      return false
    }

    finally {
      const enrichedItem = cartStore.carts
        .flatMap(cart => cart.items.map(item => ({ item, supplierName: (cart.supplier as any)?.name || '' })))
        .find(({ item: cartItem }) => cartItem.handle === item.handle && cartItem.reference === item.reference)

      if (enrichedItem) {
        const newQuantity = enrichedItem.item.quantity ?? 0
        if (newQuantity < previousQuantity) {
          trackRemoveFromCart(enrichedItem.item, { name: enrichedItem.supplierName }, { index: trackingCtx?.index, pageSection: trackingCtx?.pageSection })
        }
        else {
          trackAddToCart(enrichedItem.item, { name: enrichedItem.supplierName }, trackingCtx)
        }
      }
    }
  }
  // Delete an Item from the Cart (when quantity is 0)
  async function deleteItem(
    supplierHandle: string,
    itemHandle: string,
    reference: string,
    showSnackbar: boolean = true,
    trackingCtx?: { index?: number, pageSection?: string },
  ) {
    const cart = cartStore.carts.find(c => c.supplier?.handle === supplierHandle)
    const snapshotItem = cart?.items.find(
      item => item.handle === itemHandle && item.reference === reference,
    )
    const snapshotSupplierName = cart?.supplier?.name ?? ''

    try {
      await cartStore.updateCartItem(supplierHandle, {
        handle: itemHandle,
        quantity: 0,
        reference: reference as 'OFFICE' | 'PHARMACY',
      })

      if (showSnackbar) {
        $snackbar.showMessage({
          text: $i18n.t('snackbar.success.delete'),
          color: 'success',
          closeText: $i18n.t('close'),
        })
      }
      return true
    }
    catch (error) {
      handleCartError(error, 'Failed to delete item')
      return false
    }
    finally {
      if (snapshotItem) {
        trackRemoveFromCart(snapshotItem, { name: snapshotSupplierName }, { index: trackingCtx?.index, pageSection: trackingCtx?.pageSection })
      }
    }
  }

  // Handle errors for cart actions
  function handleCartError(error: any, logMessage: string) {
    const is404 = error.response?.status === 404
    const textKey = is404
      ? 'error.cart.validation.productIsNotAvailableAnymore'
      : 'errors.page.unknown.subtitle'
    const snackbarMessage = $i18n.t(textKey)

    $snackbar.showMessage({
      text: snackbarMessage,
      textKey,
      errorType: is404 ? 'business' : 'network',
      color: 'error',
      closeText: $i18n.t('close'),
    })

    if (is404) {
      // Business error: surfaced to the user via snackbar, no alarm needed.
      $log('warn', {
        title: 'Cart business (product unavailable)',
        message: logMessage,
        threadName: 'useCartActions.ts',
        traceId: error.data?.data?.traceId,
      })
      return
    }

    $storeErrorLog('useCartActions.ts', logMessage, snackbarMessage, error)
  }

  return {
    addOrUpdateItem,
    deleteItem,
  }
}
